import React, { useState, useEffect } from "react";
import { Button, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { EyeFill, ListUl, PencilSquare } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { TableTemplate } from "../../../reusableComponent/tableListing/cmstemplate";
import Pagination from "../../../Pagination";
import AddTagModal from "./AddTagModal";
import AddArticleModal from "./AddArticleModal";
import request from "../../../../services/requestService";
import { uploadFile } from "../../../../services/apiServices";
import { FormDataForImage } from "../../../../helper";
import { errorToastStyle, successToastStyle } from "../../../../customToast";
import { ERROR_MSG, PATHS } from "../../../../constant";
import { encodeArticleSlug } from "../../../articleDetail/articleSlug";
import { PAGE_COUNT } from "../../../userManagement/helper";
import htmlToFormattedText from "html-to-formatted-text";
import { ConfirmationComponent } from "../../../ConfirmationComponent";
import {
  addArticleAction,
  addTagsAction,
  addTags,
  getArticlesAction,
  updateArticleAction
} from "../../../../actions/articles.action";

const articlesHeader = [
  { title: "S No." },
  { title: "Title" },
  { title: "Description" },
  { title: "Upload Date" },
  { title: "Action" }
];

const ArticleEditIcon = ({ className = "", ...props }) => (
  <svg
    className={className}
    {...props}
    width="15"
    height="17"
    viewBox="0 0 15 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M1.31548 14.433C1.21143 14.4328 1.10855 14.411 1.01339 14.3689C0.918224 14.3268 0.832864 14.2654 0.762731 14.1885C0.691329 14.1123 0.636846 14.0219 0.602852 13.9231C0.568858 13.8244 0.556123 13.7195 0.565481 13.6155L0.749231 11.595L9.23773 3.10952L11.8905 5.76152L3.40423 14.2463L1.38373 14.43C1.36104 14.4321 1.33827 14.4331 1.31548 14.433ZM12.42 5.23127L9.76798 2.57927L11.3587 0.98852C11.4284 0.918787 11.5111 0.863468 11.6021 0.825725C11.6932 0.787982 11.7908 0.768555 11.8894 0.768555C11.9879 0.768555 12.0855 0.787982 12.1766 0.825725C12.2676 0.863468 12.3503 0.918787 12.42 0.98852L14.0107 2.57927C14.0805 2.64892 14.1358 2.73164 14.1735 2.82269C14.2113 2.91374 14.2307 3.01133 14.2307 3.10989C14.2307 3.20846 14.2113 3.30605 14.1735 3.3971C14.1358 3.48815 14.0805 3.57086 14.0107 3.64052L12.4207 5.23052L12.42 5.23127Z"
      fill="#E86435"
    />
    <line x1="0.25" y1="16.5" x2="11.5" y2="16.5" stroke="#E86435" />
  </svg>
);

const articlesPaginationParams = {
  offset: 0,
  limit: PAGE_COUNT,
  active: true
};

const initialArticleForm = {
  title: "",
  summary: "",
  tag: [],
  submittedBy: "",
  profileLink: "",
  content: "",
  image: null,
  imageError: "",
  imageId: null,
  imageName: "",
  imageExtension: "",
  isActive: true,
  isUploadingImage: false,
  mandatoryFields: false,
  errors: {
    title: { error: false, message: "" },
    summary: { error: false, message: "" },
    submittedBy: { error: false, message: "" },
    profileLink: { error: false, message: "" },
    content: { error: false, message: "" },
    image: { error: false, message: "" }
  }
};

const validateArticleField = (value, label) => {
  if (!value) {
    return {
      error: true,
      message: `Please enter ${label}`
    };
  }
  return {
    error: false,
    message: ""
  };
};

const validateLinkedInProfileUrl = value => {
  if (!value) {
    return {
      error: false,
      message: ""
    };
  }

  try {
    const url = new URL(value.trim());
    const allowedHosts = ["linkedin.com", "www.linkedin.com"];
    const isValidLinkedInUrl =
      url.protocol === "https:" &&
      allowedHosts.includes(url.hostname.toLowerCase()) &&
      url.pathname.startsWith("/");

    if (isValidLinkedInUrl) {
      return { error: false, message: "" };
    }
  } catch (error) {
    // Fall through to the common validation message.
  }

  return {
    error: true,
    message:
      "Enter a valid LinkedIn URL (e.g. https://www.linkedin.com/in/username)"
  };
};

const getArticleImageDetails = article => {
  const image =
    (Array.isArray(article.images) && article.images[0]) ||
    (Array.isArray(article.image) && article.image[0]) ||
    (article.image && typeof article.image === "object" ? article.image : null);
  const imageId =
    article.imageId ||
    (Array.isArray(article.imageIds)
      ? article.imageIds[0]
      : article.imageIds) ||
    (image && image.id) ||
    null;
  const imageName =
    article.imageName ||
    (image && (image.name || image.fileName || image.originalName)) ||
    "";
  const imageExtension =
    article.imageExtension ||
    (imageName && imageName.includes(".")
      ? imageName
          .split(".")
          .pop()
          .toUpperCase()
      : "");

  return {
    image,
    imageId,
    imageName,
    imageExtension
  };
};

const getArticleTags = article => {
  const articleTags = article.tags || article.tag || article.tagIds || [];
  if (!Array.isArray(articleTags)) return [];

  return articleTags
    .map(tag => {
      if (typeof tag === "string" || typeof tag === "number") {
        return {
          label: String(tag),
          value: tag
        };
      }

      if (!tag) return null;

      const label = tag.name || tag.label || tag.title || tag.value || tag.id;
      const value = tag.value || tag.id || tag.name || label;

      return label
        ? {
            label,
            value
          }
        : null;
    })
    .filter(Boolean);
};

const getTagLabel = tag =>
  typeof tag === "string" || typeof tag === "number"
    ? String(tag)
    : (tag && (tag.label || tag.name || tag.title || tag.value || tag.id)) ||
      "";

const getTagValue = tag => {
  if (!tag || typeof tag !== "object") return tag;

  const keys = ["value", "id", "tagId", "tag_id", "name"];

  for (const key of keys) {
    const val = tag[key];
    if (val !== undefined && val !== null && val !== "") {
      return val;
    }
  }

  return undefined;
};

const getTagOption = tag => {
  const label = getTagLabel(tag);
  const value = getTagValue(tag);

  return label && value !== undefined && value !== null && value !== ""
    ? {
        label,
        value
      }
    : null;
};

const getBooleanValue = (value, fallback = true) => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "string") return value.toLowerCase() === "true";
  if (typeof value === "number") return value === 1;
  return Boolean(value);
};

const getArticleFormFromArticle = article => {
  const imageDetails = getArticleImageDetails(article || {});

  return {
    ...initialArticleForm,
    id: article && article.id,
    title: (article && article.title) || "",
    summary: (article && (article.shortSummary || article.summary)) || "",
    tag: getArticleTags(article || {}),
    submittedBy:
      (article && (article.submittedByName || article.submittedBy)) || "",
    profileLink:
      (article && (article.linkedinProfileLink || article.profileLink)) || "",
    content: (article && (article.articleContent || article.content)) || "",
    isActive: getBooleanValue(
      article && article.isActive !== undefined
        ? article.isActive
        : article && article.active
    ),
    ...imageDetails
  };
};

const getArticlesListFromResponse = response => {
  const responseData = response && response.data;
  const nestedData = responseData && responseData.data;
  const articlePayload =
    nestedData && !Array.isArray(nestedData) ? nestedData : responseData;
  const articleKeys = ["articles", "list", "content", "articleDTOList"];

  if (Array.isArray(response)) return response;
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(nestedData)) return nestedData;

  for (const key of articleKeys) {
    if (articlePayload && Array.isArray(articlePayload[key])) {
      return articlePayload[key];
    }
  }

  return [];
};

const getArticlesTotalFromResponse = (response, articlesList) => {
  const responseData = response && response.data;
  const nestedData = responseData && responseData.data;
  const totalKeys = [
    "total_count",
    "totalRecords",
    "total_records",
    "totalCount",
    "totalElements",
    "total_elements",
    "totalItems",
    "total_items",
    "recordsTotal",
    "records_total",
    "count",
    "total"
  ];
  const payloads = [nestedData, responseData, response];
  let total;

  for (const payload of payloads) {
    if (!payload) continue;

    for (const key of totalKeys) {
      if (payload[key] !== undefined && payload[key] !== null) {
        total = Number(payload[key]);
        break;
      }
    }

    if (!Number.isNaN(total) && total !== undefined) break;
  }

  return total || articlesList.length;
};

const getArticlesTotalPagesFromResponse = response => {
  const responseData = response && response.data;
  const nestedData = responseData && responseData.data;
  const pageCountKeys = ["total_pages", "totalPages", "pageCount"];
  const payloads = [nestedData, responseData, response];
  let pageCount;

  for (const payload of payloads) {
    if (!payload) continue;

    for (const key of pageCountKeys) {
      if (payload[key] !== undefined && payload[key] !== null) {
        pageCount = Number(payload[key]);
        break;
      }
    }

    if (!Number.isNaN(pageCount) && pageCount !== undefined) break;
  }

  return pageCount || 0;
};

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [paginationOffset, setPaginationOffset] = useState(0);
  const [articlesTotalCount, setArticlesTotalCount] = useState(0);
  const [articlesTotalPages, setArticlesTotalPages] = useState(0);
  const [articlePagination, setArticlePagination] = useState({
    ...articlesPaginationParams
  });
  const [showTagModal, setShowTagModal] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [tags, setTags] = useState([]);
  const [articleForm, setArticleForm] = useState(initialArticleForm);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    fetchArticles(articlePagination);
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await addTagsAction({ active: true });
      const responseData = response && response.data;
      const responseDataData = responseData && responseData.data;
      const tagsList = Array.isArray(response)
        ? response
        : Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseDataData)
        ? responseDataData
        : [];
      const tagOptions = tagsList.map(getTagOption).filter(Boolean);
      setTags(tagOptions);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const closeTagModal = () => {
    if (newTag.trim() !== "") {
      const confirmClose = window.confirm(
        "You have unsaved changes. Are you sure you want to leave?"
      );

      if (!confirmClose) {
        return;
      }
    }

    setShowTagModal(false);
    fetchTags();
    setNewTag("");
  };

  const openTagModal = () => {
    setShowTagModal(true);
  };

  const handleActiveArticlesToggle = async () => {
    const nextActive = !isActive;
    const nextPagination = {
      ...articlePagination,
      offset: 0,
      active: nextActive
    };

    setIsActive(nextActive);
    setPaginationOffset(0);
    setArticlePagination(nextPagination);
    await fetchArticles(nextPagination);
  };

  const fetchArticles = async params => {
    console.log("Calling API with params:", params);
    setLoading(true);
    setArticles([]);
    try {
      const response = await getArticlesAction(params);
      if (response && response.status) {
        const articlesList = getArticlesListFromResponse(response);
        setArticles(articlesList);
        setArticlesTotalCount(
          getArticlesTotalFromResponse(response, articlesList)
        );
        setArticlesTotalPages(getArticlesTotalPagesFromResponse(response));
      } else {
        setArticles([]);
        setArticlesTotalCount(0);
        setArticlesTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
      setArticles([]);
      setArticlesTotalCount(0);
      setArticlesTotalPages(0);
      toast.error(
        <p className="toast-text-alignment">Failed to load articles</p>,
        errorToastStyle
      );
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = page => {
    const nextPagination = {
      ...articlePagination,
      offset: page.selected || 0,
      limit: PAGE_COUNT
    };

    setPaginationOffset(page.selected || 0);
    setArticlePagination(nextPagination);
    fetchArticles(nextPagination);
  };

  const onFormFieldChange = (field, value) => {
    let nextError = null;

    if (field === "title") {
      nextError = validateArticleField(value && value.trim(), "Title");
      // } else if (field === "summary") {
      //   nextError = validateArticleField(value && value.trim(), "Short Summary");
    } else if (field === "submittedBy") {
      nextError = validateArticleField(value && value.trim(), "Submitted By");
    } else if (field === "profileLink") {
      nextError = validateLinkedInProfileUrl(value && value.trim());
    } else if (field === "content") {
      const plainContent = (value || "")
        .replace(/<(.|\n)*?>/g, " ")
        .replace(/&nbsp;/g, " ")
        .trim();
      nextError = validateArticleField(plainContent, "Article Content");
    }

    setArticleForm(prevState => ({
      ...prevState,
      [field]: value,
      errors: nextError
        ? {
            ...prevState.errors,
            [field]: nextError
          }
        : prevState.errors
    }));
  };

  const handleAddTag = () => {
    const trimmedTag = newTag.trim();
    if (!trimmedTag) return;
    setTags(prevState => [
      ...prevState,
      {
        label: trimmedTag,
        value: trimmedTag
      }
    ]);
    setNewTag("");
  };

  const handleRemoveTag = indexToRemove => {
    setTags(prevState =>
      prevState.filter((_, index) => index !== indexToRemove)
    );
  };

  const closeArticleModal = () => {
    setShowArticleModal(false);
    setEditingArticle(null);
    setArticleForm(initialArticleForm);
  };

  const openAddArticleModal = () => {
    setEditingArticle(null);
    setArticleForm(initialArticleForm);
    setShowArticleModal(true);
  };

  const openEditArticleModal = article => {
    setEditingArticle(article);
    setArticleForm(getArticleFormFromArticle(article));
    setShowArticleModal(true);
  };

  const openArticleDetail = article => {
    if (article && article.id) {
      window.open(
        `${PATHS.ARTICLES_DETAILS_PAGE}/${encodeArticleSlug(
          article.id,
          article.title
        )}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const handleSaveTags = async () => {
    if (!tags.length) {
      toast.error(
        <p className="toast-text-alignment">
          Please add at least one tag before saving.
        </p>,
        errorToastStyle
      );
      return;
    }

    const payload = tags
      .map(getTagLabel)
      .filter(Boolean)
      .map(name => ({ name }));

    try {
      const response = await addTags(payload);
      if (response && response.status) {
        toast.success(
          <p className="toast-text-alignment">Tags saved successfully</p>,
          successToastStyle
        );
        setShowTagModal(false);
        fetchTags();
      } else {
        throw new Error("Unable to save tags");
      }
    } catch (error) {
      console.error("Error saving tags:", error);
      toast.error(
        <p className="toast-text-alignment">Failed to save tags</p>,
        errorToastStyle
      );
    }
    setNewTag("");
  };

  const handleArticleImage = files => {
    const file = files && files[0];
    if (!file) return;

    setArticleForm(prevState => ({
      ...prevState,
      imageError: "",
      isUploadingImage: true
    }));

    const attachmentPayload = FormDataForImage(files, "file");
    attachmentPayload.append("isMis", false);

    request(uploadFile(attachmentPayload))
      .then(response => {
        const status = response && response.data && response.data.status;
        const message = response && response.data && response.data.message;
        const data = response && response.data && response.data.data;

        if (status && data) {
          setArticleForm(prevState => ({
            ...prevState,
            image: data,
            imageError: "",
            imageId: data.id,
            imageName: data.name,
            imageExtension: (file.extension || "").toUpperCase(),
            isUploadingImage: false,
            errors: {
              ...prevState.errors,
              image: {
                error: false,
                message: ""
              }
            }
          }));
          toast.success(
            <p className="toast-text-alignment">{message}</p>,
            successToastStyle
          );
          return;
        }

        setArticleForm(prevState => ({
          ...prevState,
          image: null,
          imageId: null,
          imageName: "",
          imageExtension: "",
          imageError: message || "Unable to upload image.",
          isUploadingImage: false,
          errors: {
            ...prevState.errors,
            image: {
              error: true,
              message: message || "Please upload Image"
            }
          }
        }));
        toast.error(
          <p className="toast-text-alignment">
            {message || "Unable to upload image."}
          </p>,
          errorToastStyle
        );
      })
      .catch(error => {
        const message =
          (error && error.message) || "Something went wrong while uploading.";
        setArticleForm(prevState => ({
          ...prevState,
          image: null,
          imageId: null,
          imageName: "",
          imageExtension: "",
          imageError: message,
          isUploadingImage: false,
          errors: {
            ...prevState.errors,
            image: {
              error: true,
              message
            }
          }
        }));
        toast.error(
          <p className="toast-text-alignment">{message}</p>,
          errorToastStyle
        );
      });
  };

  const handleArticleImageError = error => {
    let errorMessage =
      "Invalid file. Please upload .png, .jpg or .jpeg up to 10MB.";

    if (error && error.message) {
      if (error.message.includes("too large")) {
        errorMessage = "The File Exceeds The Maximum Size Limit Of 10MB";
      } else if (error.message.includes("valid file type")) {
        errorMessage = "Invalid File Type";
      } else {
        errorMessage = error.message;
      }
    }

    setArticleForm(prevState => ({
      ...prevState,
      image: null,
      imageId: null,
      imageName: "",
      imageExtension: "",
      imageError: errorMessage,
      isUploadingImage: false,
      errors: {
        ...prevState.errors,
        image: {
          error: true,
          message: errorMessage
        }
      }
    }));
    toast.error(
      <p className="toast-text-alignment">{errorMessage}</p>,
      errorToastStyle
    );
  };

  const handleRemoveArticleImage = () => {
    setArticleForm(prevState => ({
      ...prevState,
      image: null,
      imageError: "",
      imageId: null,
      imageName: "",
      imageExtension: "",
      isUploadingImage: false,
      errors: {
        ...prevState.errors,
        image: {
          error: false,
          message: ""
        }
      }
    }));
    toast.success(
      <p className="toast-text-alignment">File Removed Successfully</p>,
      successToastStyle
    );
  };

  const validateArticleForm = () => {
    const contentValue = htmlToFormattedText(articleForm.content || "").trim();
    // const summaryValue = (articleForm.summary || "").trim();
    // const isSummaryLimitExceeded = summaryValue.length > 400;

    const errors = {
      title: validateArticleField(
        articleForm.title && articleForm.title.trim(),
        "Title"
      ),
      // summary: validateArticleField(summaryValue, "Short Summary"),
      summary: { error: false, message: "" },
      submittedBy: validateArticleField(
        articleForm.submittedBy && articleForm.submittedBy.trim(),
        "Submitted By"
      ),
      content: validateArticleField(contentValue, "Article Content"),
      image: articleForm.imageId
        ? { error: false, message: "" }
        : { error: true, message: "Please upload Image" }
      // image: { error: false, message: "" }
    };

    // if (!errors.summary.error && isSummaryLimitExceeded) {
    //   errors.summary = {
    //     error: true,
    //     message: "Maximum 400 characters allowed"
    //   };
    // }

    const isFormValid = !Object.values(errors).some(item => item.error);

    setArticleForm(prevState => ({
      ...prevState,
      mandatoryFields: !isFormValid,
      imageError: errors.image.error
        ? errors.image.message
        : prevState.imageError,
      errors
    }));

    return isFormValid;
  };

  const handleArticlePreview = event => {
    event && event.preventDefault();
    const isValidData = validateArticleForm();
    if (isValidData) {
      return true;
    } else {
      toast.error(
        <p className="toast-text-alignment">{ERROR_MSG.INVALID_FORM}</p>,
        errorToastStyle
      );
    }

    return false;
  };

  const handleArticleConfirm = async event => {
    event && event.preventDefault();
    try {
      //payload
      const payload = {
        title: articleForm.title,
        // shortSummary: articleForm.summary,
        tagIds: (articleForm.tag || []).map(tag => tag.value),
        submittedByName: articleForm.submittedBy,
        linkedinProfileLink: articleForm.profileLink,
        articleContent: articleForm.content,
        active: Boolean(articleForm.isActive),
        imageIds: [articleForm.imageId] // Or full image data if required
        // imageIds: [42] // Or full image data if required
      };

      // Calling api
      const response =
        editingArticle && editingArticle.id
          ? await updateArticleAction(payload, editingArticle.id)
          : await addArticleAction(payload);
      // Handle success
      if (response && response.status) {
        toast.success(
          <p className="toast-text-alignment">
            {response.message ||
              (editingArticle
                ? "Article updated successfully"
                : "Article submitted successfully")}
          </p>,
          successToastStyle
        );

        // Reset form and close
        setArticleForm(initialArticleForm);
        setEditingArticle(null);
        setShowArticleModal(false);
        setShowAlert(false);
        // Refetch articles to update the list
        await fetchArticles(articlePagination);
      } else {
        throw new Error((response && response.message) || "Submission failed");
      }
    } catch (error) {
      // Handle error
      const errorMessage =
        (error && error.message) || "Something went wrong while submitting.";
      toast.error(
        <p className="toast-text-alignment">{errorMessage}</p>,
        errorToastStyle
      );
    }
  };

  const pageCount =
    articlesTotalPages || Math.ceil(articlesTotalCount / PAGE_COUNT);

  return (
    <div className="group-management users-listing">
      <div className="header">
        <div className="d-flex flex-row">
          <h3>
            Articles{" "}
            {articlesTotalCount !== 0 ? (
              <span style={{ verticalAlign: "middle" }}>
                ({articlesTotalCount})
              </span>
            ) : (
              ""
            )}
          </h3>
          <Form.Check
            className={`form-checkbox cms-articles-active-filter ${
              isActive === true ? "checked" : ""
            }`}
          >
            <Form.Check.Input type="checkbox" />
            <Form.Check.Label
              className="d-flex active-check-mark"
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                handleActiveArticlesToggle();
              }}
            >
              <div className="active-check-label">ACTIVE ARTICLES</div>
            </Form.Check.Label>
          </Form.Check>
        </div>

        <div className="header-buttons d-flex flex-row">
          <OverlayTrigger
            trigger="hover"
            defaultShow={false}
            placement="bottom"
            overlay={
              <Tooltip id="tooltip-add-tag" className="create_project_tolltip">
                Manage Tags
              </Tooltip>
            }
          >
            <Button
              className="btn-orange create-btn cms-articles-list-button"
              size="sm"
              onClick={openTagModal}
            >
              <div>
                <ListUl />
              </div>
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            trigger="hover"
            defaultShow={false}
            placement="bottom"
            overlay={
              <Tooltip
                id="tooltip-add-article"
                className="create_project_tolltip"
              >
                Add Article
              </Tooltip>
            }
          >
            <Button
              className="btn-orange create-btn cms-articles-list-button"
              size="sm"
              onClick={openAddArticleModal}
            >
              <div>
                <PencilSquare />
              </div>
            </Button>
          </OverlayTrigger>
        </div>
      </div>

      <TableTemplate
        isUsersTable={true}
        isBestPracticesTable={true}
        headersOptions={articlesHeader}
        classes="cms-articles-list-table"
        isLoading={loading}
        checkboxLoader={loading}
        bodyOptions={articles}
        bodyTemplate={(article, index) => (
          <>
            <td>
              {index + 1 + articlePagination.offset * articlePagination.limit}
            </td>
            <td className="pl-2">
              <OverlayTrigger
                placement="top-start"
                overlay={
                  <Tooltip
                    // id={`cms-article-title-tooltip-${article.id || index}`}
                    className="mis-tooltip"
                    placement="top-start"
                  >
                    {article.title}
                  </Tooltip>
                }
              >
                <div
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    whiteSpace: "pre-line",
                    color: "#727272"
                  }}
                >
                  {article.title}
                </div>
              </OverlayTrigger>
            </td>
            <td>
              <div
                className="cms-articles-summary-text"
                style={{ cursor: "pointer" }}
              >
                {article.shortSummary}
              </div>
            </td>
            <td className="cms-articles-list-date">{article.createdAt}</td>
            <td className="cms-articles-list-actions">
              <div className="cms-articles-list-action-list">
                <button
                  type="button"
                  className="cms-articles-list-action-btn"
                  aria-label={`Edit article ${article.id}`}
                  onClick={() => openEditArticleModal(article)}
                >
                  <ArticleEditIcon className="cms-articles-list-action-icon" />
                </button>
                <button
                  type="button"
                  className="cms-articles-list-action-btn"
                  aria-label={`View article ${article.id}`}
                  onClick={() => openArticleDetail(article)}
                >
                  <EyeFill className="cms-articles-list-action-icon" />
                </button>
              </div>
            </td>
          </>
        )}
        headersTemplate={header => (
          <>
            <th>{header.title}</th>
          </>
        )}
      />
      {!loading ? (
        <div className="pagination-wrapper">
          <div className="pagination pagination-in-user-listing">
            <Pagination
              offset={paginationOffset}
              pageCount={pageCount}
              onPageChange={onPageChange}
            />
          </div>
        </div>
      ) : null}
      {/* {loading && (
        <div className="text-center mt-3">
          <p>Loading articles...</p>
        </div>
      )} */}

      <AddTagModal
        show={showTagModal}
        onHide={closeTagModal}
        newTag={newTag}
        onNewTagChange={event => setNewTag(event.target.value)}
        onAddTag={handleAddTag}
        tags={tags}
        onRemoveTag={handleRemoveTag}
        onSaveTags={handleSaveTags}
      />

      <AddArticleModal
        show={showArticleModal}
        onHide={closeArticleModal}
        articleForm={articleForm}
        onFormFieldChange={onFormFieldChange}
        tags={tags}
        onArticleImage={handleArticleImage}
        onArticleImageError={handleArticleImageError}
        onArticlePreview={handleArticlePreview}
        onArticleSubmit={handleArticleConfirm}
        onRemoveArticleImage={handleRemoveArticleImage}
        isEdit={Boolean(editingArticle)}
      />
      <ConfirmationComponent
        show={showAlert}
        modalClass={"confirmation-modal"}
        saveHandler={handleArticleConfirm}
        closeHandler={() => setShowAlert(false)}
        content={
          editingArticle
            ? "Are you sure you want to update this article?"
            : "Are you sure you want to submit this article?"
        }
      />
    </div>
  );
};

export default Articles;
